import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/providers/auth";
import api from "@/api/v1-jwt/config/api";
import { CreateInvoiceFormData } from "./schema";
import { formatPhoneUS } from "@/utils/functions";
import { Button } from "@/components/ui/button-tw";
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { InvoiceFrequency } from "@/queries/invoice/types";

interface BusinessInfoOverride {
  businessName?: string;
  businessEmail?: string;
  businessAddress?: string;
  businessAddressLine2?: string;
  businessCity?: string;
  businessState?: string;
  businessZip?: string;
  hideAddress?: boolean;
}

interface BrandingOverride {
  logo?: string;
  brandColor?: string;
  secondaryColor?: string;
}

interface IframeInvoicePreviewProps {
  formData: CreateInvoiceFormData;
  zoomLevel?: number;
  showZoomControls?: boolean;
  businessInfoOverride?: BusinessInfoOverride;
  brandingOverride?: BrandingOverride;
}

const escapeHtml = (str: string | undefined): string =>
  (str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export function IframeInvoicePreview({
  formData,
  zoomLevel = 1,
  showZoomControls = false,
  businessInfoOverride,
  brandingOverride,
}: IframeInvoicePreviewProps) {
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const [base64Template, setBase64Template] = useState<string>("");
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [isIframeReady, setIsIframeReady] = useState<boolean>(false);
  const [currentZoom, setCurrentZoom] = useState<number>(zoomLevel);
  const [containerSize, setContainerSize] = useState({
    width: 794,
    height: 1123,
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PAGE_COUNT_RESPONSE') {
        const pages = event.data.pages || 1;
        setTotalPages(pages);
        
        // Reset to first page if current page exceeds total
        if (currentPage > pages) {
          setCurrentPage(1);
          // Send message to show first page
          const iframe = iframeRef.current;
          if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage({ type: 'SHOW_PAGE', page: 1 }, '*');
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [currentPage]);

  const zoomIn = useCallback(() => {
    setCurrentZoom((prev) => Math.min(prev + 0.25, 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setCurrentZoom((prev) => Math.max(prev - 0.25, 0.25));
  }, []);

  const resetZoom = useCallback(() => {
    setCurrentZoom(1.0); // Reset to 100%
  }, []);

  const goToPrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      updatePageVisibility(currentPage - 1);
    }
  }, [currentPage]);

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      updatePageVisibility(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  // Update page visibility in iframe
  const updatePageVisibility = useCallback((pageNum: number) => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;

    try {
      iframe.contentWindow.postMessage({ type: 'SHOW_PAGE', page: pageNum }, '*');
    } catch (error) {
      console.error("Error updating page visibility:", error);
    }
  }, []);

  useEffect(() => {
    api
      .get(`/platform/invoices/${userId}/preview/template`)
      .then((response) => {
        if (response.data?.base64_html) {
          setBase64Template(response.data.base64_html);
        }
      })
      .catch((error) => console.error("Error fetching template:", error));
  }, [userId]);

  const formatCurrency = useCallback((amount: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  const formatTerms = useCallback((terms: string) => {
    if (!terms) return "";
    return terms
      .replace(/-/g, " ") // Remove dashes
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
      .join(" ");
  }, []);

  const generateInvoiceHtml = useCallback(
    (template: string, data: CreateInvoiceFormData) => {
      if (!template) return "";

      try {
        let html = atob(template);

        // Replace business information if override is provided
        if (businessInfoOverride) {
          if (businessInfoOverride.businessName) {
            html = html.replace(/\$\{business_name\}/g, businessInfoOverride.businessName);
          }
          if (businessInfoOverride.businessEmail) {
            html = html.replace(/\$\{business_email\}/g, businessInfoOverride.businessEmail);
          }
          if (!businessInfoOverride.hideAddress) {
            html = html.replace(/\$\{business_address\}/g, escapeHtml(businessInfoOverride.businessAddress));
            html = html.replace(/\$\{business_address_line_2\}/g, escapeHtml(businessInfoOverride.businessAddressLine2));
            const businessCityStateZip = [
              businessInfoOverride.businessCity,
              businessInfoOverride.businessState,
              businessInfoOverride.businessZip,
            ]
              .filter(Boolean)
              .join(", ")
              .replace(/, ,/g, ",")
              .trim();
            html = html.replace(/\$\{business_city_state_zip\}/g, businessCityStateZip);
          } else {
            html = html.replace(/\$\{business_address\}/g, "");
            html = html.replace(/\$\{business_address_line_2\}/g, "");
            html = html.replace(/\$\{business_city_state_zip\}/g, "");
          }
        }

        html = html.replace(/\$\{invoice_number\}/g, data.invoiceNumber || "");
        html = html.replace(/\$\{invoice_term\}/g, formatTerms(data.terms || ""));
        html = html.replace(/\$\{invoice_notes\}/g, data.notes || "");

        const fullName = `${data.invoiceTo?.first_name || ""} ${
          data.invoiceTo?.last_name || ""
        }`.trim();
        html = html.replace(
          /\$\{customer_name\}/g,
          data.invoiceTo?.business_dba ||
            data.invoiceTo?.business_name ||
            fullName ||
            ""
        );
        html = html.replace(
          /\$\{customer_email\}/g,
          data.invoiceTo?.email || ""
        );
        html = html.replace(
          /\$\{customer_phone\}/g,
          data.invoiceTo?.phone ? formatPhoneUS(data.invoiceTo?.phone) : ""
        );
        html = html.replace(
          /\$\{customer_address\}/g,
          data.invoiceTo?.business_address || ""
        );

        const cityStateZip = [
          data.invoiceTo?.business_city,
          data.invoiceTo?.business_state,
          data.invoiceTo?.business_zip,
        ]
          .filter(Boolean)
          .join(", ")
          .replace(/, ,/g, ",")
          .trim();

        html = html.replace(/\$\{customer_city_state_zip\}/g, cityStateZip);

        const issueDate = data.sendDate
          ? new Date(data.sendDate).toLocaleDateString()
          : new Date().toLocaleDateString();
        const dueDate = data.dueDate
          ? new Date(data.dueDate).toLocaleDateString()
          : "";
        html = html.replace(/\$\{invoice_date\}/g, issueDate);
        html = html.replace(/\$\{due_date\}/g, dueDate);

        let subtotal = 0;
        let itemsHtml = "";
        
        if (data.lineItems && data.lineItems.length > 0) {
          data.lineItems.forEach((item) => {
            const itemTotal = (item.quantity || 0) * (item.unit_price || 0);
            subtotal += itemTotal;

            itemsHtml += `
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${
                item.description || ""
              }</td>
              <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: center;">${
                item.quantity || 0
              }</td>
              <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right;">${formatCurrency(
                item.unit_price || 0
              )}</td>
              <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right;">${formatCurrency(
                itemTotal
              )}</td>
            </tr>
          `;
          });
        }

        // Always replace line_items placeholder (with empty string if no items)
        html = html.replace(/\$\{line_items\}/g, itemsHtml);
        html = html.replace(
          /\$\{invoice_subtotal\}/g,
          formatCurrency(subtotal)
        );

        const taxAmount =
          data.taxAmount || ((data.taxPercentage || 0) * subtotal) / 100;
        const shippingFee = data.shippingFee || 0;
        const total = subtotal + taxAmount + shippingFee;

        html = html.replace(/\$\{invoice_tax\}/g, formatCurrency(taxAmount));
        html = html.replace(
          /\$\{invoice_shipping_fee\}/g,
          formatCurrency(shippingFee)
        );
        html = html.replace(/\$\{invoice_total\}/g, formatCurrency(total));

        return html;
      } catch (error) {
        console.error("Error generating invoice HTML:", error);
        return "";
      }
    },
    [formatCurrency, formatTerms]
  );

  // Track previous values for change detection
  const previousValues = useRef<Record<string, any>>({});

  const updateIframeFields = useCallback(
    (data: CreateInvoiceFormData) => {
      const iframe = iframeRef.current;
      if (!iframe || !iframe.contentWindow) return;

      try {
        // Prepare update data
        const fullName = `${data.invoiceTo?.first_name || ""} ${
          data.invoiceTo?.last_name || ""
        }`.trim();
        const customerName =
          data.invoiceTo?.business_dba ||
          data.invoiceTo?.business_name ||
          fullName ||
          "";

        const phone = data.invoiceTo?.phone
          ? formatPhoneUS(data.invoiceTo?.phone)
          : "";

        const issueDate = data.sendDate
          ? new Date(data.sendDate).toLocaleDateString()
          : new Date().toLocaleDateString();
        const dueDate = data.dueDate
          ? new Date(data.dueDate).toLocaleDateString()
          : "";

        let subtotal = 0;
        let lineItems: Array<{
          description: string;
          quantity: number;
          unit_price: string;
          item_total: string;
        }> = [];

        if (data.lineItems && data.lineItems.length > 0) {
          lineItems = data.lineItems.map((item) => {
            const itemTotal = (item.quantity || 0) * (item.unit_price || 0);
            subtotal += itemTotal;
            
            return {
              description: item.description || "",
              quantity: item.quantity || 0,
              unit_price: formatCurrency(item.unit_price || 0),
              item_total: formatCurrency(itemTotal)
            };
          });
        }

        const taxAmount =
          data.taxAmount || ((data.taxPercentage || 0) * subtotal) / 100;
        const shippingFee = data.shippingFee || 0;
        const total = subtotal + taxAmount + shippingFee;

        const formattedTerms = formatTerms(data.terms || "");

        const fields: any = {
          invoice_number: data.invoiceNumber || "",
          customer_name: customerName,
          customer_email: data.invoiceTo?.email || "",
          customer_phone: phone,
          invoice_date: issueDate,
          due_date: dueDate,
          invoice_term: formattedTerms,
          invoice_notes: data.notes || "",
          line_items: lineItems,
          invoice_subtotal: formatCurrency(subtotal),
          invoice_tax: formatCurrency(taxAmount),
          invoice_shipping_fee: formatCurrency(shippingFee),
          invoice_total: formatCurrency(total)
        };

        // Add business info overrides if provided
        if (businessInfoOverride) {
          if (businessInfoOverride.businessName) {
            fields.business_name = businessInfoOverride.businessName;
          }
          if (businessInfoOverride.businessEmail) {
            fields.business_email = businessInfoOverride.businessEmail;
          }
          if (!businessInfoOverride.hideAddress) {
            fields.business_address_line_1 = escapeHtml(businessInfoOverride.businessAddress);
            fields.business_address_line_2 = escapeHtml(businessInfoOverride.businessAddressLine2);
            const businessCityStateZip = [
              businessInfoOverride.businessCity,
              businessInfoOverride.businessState,
              businessInfoOverride.businessZip,
            ]
              .filter(Boolean)
              .join(", ")
              .replace(/, ,/g, ",")
              .trim();
            fields.business_city_state_zip = businessCityStateZip;
          } else {
            fields.business_address_line_1 = "";
            fields.business_address_line_2 = "";
            fields.business_city_state_zip = "";
          }
        }

        // Add branding overrides if provided
        if (brandingOverride) {
          if (brandingOverride.logo) {
            fields.logo = brandingOverride.logo;
          }
          if (brandingOverride.brandColor) {
            fields.brand_color = brandingOverride.brandColor;
          }
          if (brandingOverride.secondaryColor) {
            fields.secondary_color = brandingOverride.secondaryColor;
          }
        }

        const updateData = {
          type: 'UPDATE_FIELDS',
          fields
        };

        // Only send update if data has changed
        const updateKey = JSON.stringify(updateData.fields);
        if (previousValues.current['updateData'] !== updateKey) {
          iframe.contentWindow.postMessage(updateData, '*');
          previousValues.current['updateData'] = updateKey;
        }
      } catch (error) {
        console.error("Error preparing iframe field updates:", error);
      }
    },
    [formatCurrency, formatTerms, businessInfoOverride, brandingOverride]
  );

  const enhanceTemplateWithDataAttributes = useCallback(
    (html: string, scaleRatio: number) => {
      const enhancedHtml = html
        .replace(
          /\$\{invoice_number\}/g,
          '<span data-field="invoice_number">${invoice_number}</span>'
        )
        .replace(
          /\$\{customer_name\}/g,
          '<span data-field="customer_name">${customer_name}</span>'
        )
        .replace(
          /\$\{customer_email\}/g,
          '<span data-field="customer_email">${customer_email}</span>'
        )
        .replace(
          /\$\{customer_phone\}/g,
          '<span data-field="customer_phone">${customer_phone}</span>'
        )
        .replace(
          /\$\{invoice_date\}/g,
          '<span data-field="invoice_date">${invoice_date}</span>'
        )
        .replace(
          /\$\{due_date\}/g,
          '<span data-field="due_date">${due_date}</span>'
        )
        .replace(
          /\$\{invoice_term\}/g,
          '<span data-field="invoice_term">${invoice_term}</span>'
        )
        .replace(
          /\$\{line_items\}/g,
          '<tbody data-field="line_items">${line_items}</tbody>'
        )
        .replace(
          /\$\{invoice_subtotal\}/g,
          '<span data-field="invoice_subtotal">${invoice_subtotal}</span>'
        )
        .replace(
          /\$\{invoice_tax\}/g,
          '<span data-field="invoice_tax">${invoice_tax}</span>'
        )
        .replace(
          /\$\{invoice_shipping_fee\}/g,
          '<span data-field="invoice_shipping_fee">${invoice_shipping_fee}</span>'
        )
        .replace(
          /\$\{invoice_total\}/g,
          '<span data-field="invoice_total">${invoice_total}</span>'
        )
        .replace(
          /\$\{invoice_notes\}/g,
          '<span data-field="invoice_notes">${invoice_notes}</span>'
        );

      const scalingCSS = `
      <style>
        html, body {
          margin: 0;
          padding: 0;
          overflow: hidden;
        }
        
        #invoice-wrapper {
          width: 794px;
          transform-origin: top left;
          transform: scale(${scaleRatio});
          background: white;
          position: relative;
        }
        
        .invoice-page {
          width: 794px;
          height: 1123px;
          background: white;
          position: relative;
          overflow: hidden;
          page-break-after: always;
        }
        
        .invoice-page.hidden {
          display: none;
        }
        
        /* Preserve original styling */
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        /* Handle table splits across pages */
        table {
          break-inside: avoid;
        }
        
        tr {
          break-inside: avoid;
        }
      </style>
      <script>
        let currentPageNum = 1;
        let totalPageCount = 1;
        
        // Handle postMessage events from parent
        window.addEventListener('message', function(event) {
          if (event.data?.type === 'UPDATE_FIELDS') {
            // Handle field updates
            const fields = event.data.fields || {};

            // Handle special fields
            // Update logo
            if (fields.logo !== undefined) {
              const logoContainer = document.querySelector('.header-container');
              if (logoContainer) {
                const logoImg = logoContainer.querySelector('img');
                if (logoImg && fields.logo) {
                  logoImg.src = fields.logo;
                }
              }
            }

            // Update brand color
            if (fields.brand_color) {
              // Update header
              const header = document.querySelector('.header');
              if (header) {
                header.style.backgroundColor = fields.brand_color;
              }

              // Update all th background colors
              const ths = document.querySelectorAll('th');
              ths.forEach(function(th) {
                th.style.setProperty('background-color', fields.brand_color, 'important');
              });

              // Update bar left and bar right elements
              const barElements = document.querySelectorAll('.bar.left, .bar.right, .bar-left, .bar-right');
              barElements.forEach(function(element) {
                element.style.setProperty('background-color', fields.brand_color, 'important');
              });
            }

            // Update business information by class name
            if (fields.business_name !== undefined) {
              const businessNameElements = document.querySelectorAll('.business-name');
              businessNameElements.forEach(function(element) {
                element.textContent = fields.business_name;
              });
            }

            if (fields.business_email !== undefined) {
              const businessEmailElements = document.querySelectorAll('.business-email');
              businessEmailElements.forEach(function(element) {
                element.textContent = fields.business_email;
              });
            }

            if (fields.business_address_line_1 !== undefined) {
              document.querySelectorAll('.business-address-line-1').forEach(function(element) {
                element.textContent = fields.business_address_line_1;
              });
            }

            if (fields.business_address_line_2 !== undefined) {
              document.querySelectorAll('.business-address-line-2').forEach(function(element) {
                element.textContent = fields.business_address_line_2;
              });
            }

            if (fields.business_city_state_zip !== undefined) {
              const businessCityStateZipElements = document.querySelectorAll('.business-city-state-zip');
              businessCityStateZipElements.forEach(function(element) {
                element.textContent = fields.business_city_state_zip;
              });
            }

            // Update single fields
            Object.keys(fields).forEach(function(key) {
              if (['line_items', 'logo', 'brand_color', 'secondary_color', 'business_name', 'business_email', 'business_address_line_1', 'business_address_line_2', 'business_city_state_zip'].includes(key)) return; // Handle these separately

              const elements = document.querySelectorAll('[data-field="' + key + '"]');
              elements.forEach(function(element) {
                if (element) {
                  element.textContent = fields[key] || '';
                }
              });
            });
            
            // Handle line items specially
            if (fields.line_items && Array.isArray(fields.line_items)) {
              const lineItemsContainer = document.querySelector('[data-field="line_items"]');
              if (lineItemsContainer) {
                let itemsHtml = '';
                fields.line_items.forEach(function(item) {
                  itemsHtml += 
                    '<tr>' +
                      '<td style="padding: 8px; border-bottom: 1px solid #e5e7eb;" data-field="item_description">' + item.description + '</td>' +
                      '<td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: center;" data-field="item_quantity">' + item.quantity + '</td>' +
                      '<td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right;" data-field="item_price">' + item.unit_price + '</td>' +
                      '<td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right;" data-field="item_total">' + item.item_total + '</td>' +
                    '</tr>';
                });
                
                if (lineItemsContainer.innerHTML !== itemsHtml) {
                  lineItemsContainer.innerHTML = itemsHtml;
                }
              }
            }
          } else if (event.data?.type === 'UPDATE_SCALE') {
            // Handle scale updates
            const newScale = event.data.scale;
            const wrapper = document.getElementById('invoice-wrapper');
            if (wrapper) {
              wrapper.style.transform = 'scale(' + newScale + ')';
            }
          } else if (event.data?.type === 'SHOW_PAGE') {
            // Handle page navigation
            const pageNum = event.data.page;
            currentPageNum = pageNum;
            const pages = document.querySelectorAll('.invoice-page');
            pages.forEach(function(page, index) {
              if (index + 1 === pageNum) {
                page.classList.remove('hidden');
              } else {
                page.classList.add('hidden');
              }
            });
          } else if (event.data?.type === 'GET_PAGE_COUNT') {
            // Handle page count request
            const pages = calculatePages();
            parent.postMessage({ type: 'PAGE_COUNT_RESPONSE', pages: pages }, '*');
          }
        });
        
        // Calculate pagination based on content height
        window.calculatePages = function() {
          const wrapper = document.getElementById('invoice-wrapper');
          if (!wrapper) return 1;
          
          const content = wrapper.querySelector('.invoice-content');
          if (!content) return 1;
          
          const contentHeight = content.scrollHeight;
          const pageHeight = 1123;
          const pages = Math.ceil(contentHeight / pageHeight);
          
          // Create pages if needed
          if (pages > 1) {
            wrapper.innerHTML = '';
            for (let i = 0; i < pages; i++) {
              const page = document.createElement('div');
              page.className = 'invoice-page' + (i > 0 ? ' hidden' : '');
              page.innerHTML = content.outerHTML;
              
              // Adjust content position for each page
              const pageContent = page.querySelector('.invoice-content');
              if (pageContent) {
                pageContent.style.position = 'relative';
                pageContent.style.top = '-' + (i * pageHeight) + 'px';
              }
              
              wrapper.appendChild(page);
            }
          }
          
          totalPageCount = pages;
          return pages;
        };
        
        // Get total pages
        window.getTotalPages = function() {
          return totalPageCount;
        };

        // Initialize pagination on load
        document.addEventListener('DOMContentLoaded', function() {
          calculatePages();
        });
        
        // If already loaded, calculate immediately
        if (document.readyState === 'complete') {
          calculatePages();
        }

      </script>
    `;

      const wrappedContent = enhancedHtml.includes("<body>")
        ? enhancedHtml.replace(
            /(<body[^>]*>)(.*?)(<\/body>)/s,
            `$1<div id="invoice-wrapper"><div class="invoice-page"><div class="invoice-content">$2</div></div></div>$3`
          )
        : `<div id="invoice-wrapper"><div class="invoice-page"><div class="invoice-content">${enhancedHtml}</div></div></div>`;

      if (wrappedContent.includes("<head>")) {
        return wrappedContent.replace("</head>", `${scalingCSS}</head>`);
      } else if (wrappedContent.includes("<body>")) {
        return wrappedContent.replace("<body>", `<body>${scalingCSS}`);
      } else {
        return `${scalingCSS}${wrappedContent}`;
      }
    },
    []
  );

  // Update iframe scale when container size changes
  const updateIframeScale = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;

    const scaleX = containerSize.width / 794;
    const scaleY = containerSize.height / 1123;
    const scale = Math.min(scaleX, scaleY);

    try {
      iframe.contentWindow.postMessage({ type: 'UPDATE_SCALE', scale }, '*');
    } catch (error) {
      console.error("Error updating iframe scale:", error);
    }
  }, [containerSize]);

  // Calculate pagination after content changes
  const updatePagination = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;

    try {
      // Use postMessage to request page count
      iframe.contentWindow.postMessage({ type: 'GET_PAGE_COUNT' }, '*');
    } catch (error) {
      console.error("Error updating pagination:", error);
    }
  }, []);

  useEffect(() => {
    updateIframeScale();
  }, [updateIframeScale]);

  // Initial iframe load - only runs once when template is fetched
  useEffect(() => {
    if (!base64Template || isIframeReady) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    const onIframeLoad = () => {
      setIsInitialLoad(false);
      setIsIframeReady(true);
      // Clear previous values cache when iframe loads
      previousValues.current = {};
      updateIframeScale();

      setTimeout(() => {
        updatePagination();
      }, 100);
    };

    iframe.addEventListener("load", onIframeLoad, { once: true });

    const scaleX = containerSize.width / 794;
    const scaleY = containerSize.height / 1123;
    const initialScale = Math.min(scaleX, scaleY);

    // Create initial template without form data - we'll update via postMessage
    const enhancedHtml = enhanceTemplateWithDataAttributes(
      atob(base64Template),
      initialScale
    );
    const emptyFormData: CreateInvoiceFormData = {
      status: "draft",
      invoiceTo: { email: "" },
      notes: "",
      deliveryMethod: null,
      invoiceFrequency: InvoiceFrequency.ONE_TIME,
      terms: undefined,
      dueDate: undefined,
      sendDate: undefined,
      shippingFee: 0,
      invoiceFrequencyInterval: 0,
      invoiceNumber: null,
      invoiceExternalId: null,
      invoiceExternalIdLink: null,
      taxPercentage: 0,
      taxAmount: 0,
      lineItems: []
    };
    const fullHtml = generateInvoiceHtml(btoa(enhancedHtml), emptyFormData);

    const blob = new Blob([fullHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    iframe.src = url;

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [base64Template, containerSize, isIframeReady, updateIframeScale, enhanceTemplateWithDataAttributes, generateInvoiceHtml, updatePagination]);

  // Update iframe fields when form data changes - only after iframe is ready
  useEffect(() => {
    if (!isIframeReady || isInitialLoad) return;

    updateIframeFields(formData);
  }, [formData, isIframeReady, isInitialLoad, updateIframeFields, brandingOverride, businessInfoOverride]);

  // Debounced pagination updates (separate from field updates)
  useEffect(() => {
    if (!isIframeReady) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      updatePagination();
    }, 100);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [formData.lineItems, updatePagination, isIframeReady]);

  return (
    <div className="w-full h-full flex flex-col relative">
      <div
        className={`flex-1 flex justify-center items-start p-4 overflow-auto`}
        style={{ minHeight: 0 }}
      >
        {showZoomControls && (
          <div className="absolute top-6 right-6 flex flex-col gap-2 z-10">
            <div className="flex flex-col bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
              <Button
                onClick={zoomIn}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-gray-100 rounded-none border-b border-gray-200"
                title="Zoom in"
                disabled={currentZoom >= 3.0}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <div className="px-2 py-1 text-xs text-gray-600 text-center bg-gray-50 border-b border-gray-200 min-w-[50px]">
                {Math.round(currentZoom * 100)}%
              </div>
              <Button
                onClick={zoomOut}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-gray-100 rounded-none border-b border-gray-200"
                title="Zoom out"
                disabled={currentZoom <= 0.25}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button
                onClick={resetZoom}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-gray-100 rounded-none"
                title="Reset zoom"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <div
          className="w-full flex justify-center"
          style={{
            minWidth: currentZoom > 1 ? `${100 * currentZoom}%` : "100%",
            minHeight: currentZoom > 1 ? `${100 * currentZoom}%` : "100%",
          }}
        >
          <div
            ref={containerRef}
            className="bg-white border border-gray-200 shadow-lg rounded-lg overflow-hidden"
            style={{
              width: "100%",
              maxWidth: currentZoom <= 1 ? "1024px" : `${1024 * currentZoom}px`,
              aspectRatio: "794 / 1123", // A4 aspect ratio (width / height)
              transform: `scale(${currentZoom})`,
              transformOrigin: "top center",
            }}
          >
            <iframe
              ref={iframeRef}
              className="w-full h-full border-none"
              style={{
                display: "block",
              }}
              sandbox="allow-scripts"
              title="Invoice Preview"
            />
          </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <Button
              onClick={goToPrevPage}
              disabled={currentPage <= 1}
              variant="outline"
              size="sm"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-600">
              Page <span className="font-medium">{currentPage}</span> of{" "}
              <span className="font-medium">{totalPages}</span>
            </span>
            <Button
              onClick={goToNextPage}
              disabled={currentPage >= totalPages}
              variant="outline"
              size="sm"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
